import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  message,
  Tag,
  Modal,
  Form,
} from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import {
  getListAsync,
  updateStatusAsync,
  addAsync,
} from "../../api/userSystemPermissions";
import { getAllClient } from "../../api/idsClient";
import { getUserDropdownByLoginId } from "../../api/user";
import "./index.css";
import dayjs from "dayjs";

// 接口返回 login_id, client_id, clientName, createUserName, updateUserName 等，兼容 PascalCase
const getRecordField = (record: any, fieldCode: string): unknown => {
  const v = record[fieldCode];
  if (v !== undefined && v !== null) return v;
  const map: Record<string, string[]> = {
    LoginId: ["login_id", "loginId"],
    ClientName: ["clientName", "client_id"],
    Status_name: ["status"],
    create_user_name: ["createUserName"],
    update_user_name: ["updateUserName"],
  };
  const keys = map[fieldCode];
  if (keys) for (const k of keys) if (record[k] !== undefined) return record[k];
  return undefined;
};

const TABLE_COLUMNS_CONFIG = [
  { field_code: "LoginId", field_name: "用户名", width: 120 },
  { field_code: "ClientName", field_name: "系统", width: 140 },
  { field_code: "Status_name", field_name: "状态", width: 100 },
  { field_code: "create_user_name", field_name: "创建人", width: 100 },
  {
    field_code: "create_time",
    field_name: "创建时间",
    width: 170,
    isTime: true,
  },
  { field_code: "update_user_name", field_name: "更新人", width: 100 },
  {
    field_code: "update_time",
    field_name: "更新时间",
    width: 170,
    isTime: true,
  },
];

const UserSystemPermissions = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    LoginId: "",
    ClientId: "",
    Status: "",
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [clientList, setClientList] = useState([] as { clientId: string; clientName: string }[]);
  const [userList, setUserList] = useState([] as { loginId: string; userName: string }[]);
  const [form] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = {
        loginId: searchParams.LoginId || "",
        clientId: searchParams.ClientId || "",
        status: searchParams.Status === "" ? undefined : searchParams.Status,
      };
      const response = (await getListAsync(params)) as {
        res_status_code?: string;
        res_content?: unknown[];
        res_message?: string;
      };

      if (response?.res_status_code === "0" && response?.res_content != null) {
        const items = Array.isArray(response.res_content) ? response.res_content : [];
        setList(items);
        setPagination((prev) => ({ ...prev, total: items.length }));
      } else {
        message.error(response?.res_message || "获取列表失败");
      }
    } catch (error) {
      console.error("获取人员系统权限列表失败:", error);
      message.error("获取列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  // 与弹框一致：页面加载时拉取系统下拉数据，供搜索项与新建弹框共用
  useEffect(() => {
    getAllClient({})
      .then((res: any) => {
        if (res?.res_status_code === "0" && Array.isArray(res?.res_content)) {
          const list = res.res_content
            .map((r: any) => ({
              clientId: r.clientId ?? r.ClientId ?? "",
              clientName: r.clientName ?? r.ClientName ?? "",
            }))
            .filter((r: any) => r.clientId);
          setClientList(list);
        }
      })
      .catch((e) => console.error("获取系统列表失败", e));
  }, []);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchList();
  };

  const handleReset = () => {
    setSearchParams({ LoginId: "", ClientId: "", Status: "" });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setTimeout(() => fetchList(), 100);
  };

  const handleTableChange = (newPagination: {
    current?: number;
    pageSize?: number;
  }) => {
    setPagination({
      ...pagination,
      current: newPagination.current ?? pagination.current,
      pageSize: newPagination.pageSize ?? pagination.pageSize,
    });
  };

  const getRowId = (record: any, index?: number) => {
    const id = record.id ?? record.Id ?? record.loginId ?? record.LoginId;
    return id != null ? String(id) : `row-${index ?? 0}`;
  };

  const handleStatusChange = async (record: any, newStatus: 0 | 1) => {
    const id = record.id ?? record.Id;
    if (id == null) {
      message.warning("无法获取记录ID");
      return;
    }
    const actionText = newStatus === 1 ? "启用" : "停用";
    Modal.confirm({
      title: "操作确认",
      content: `确定要${actionText}该条权限吗？${newStatus === 0 ? "停用后该用户将无法访问对应系统。" : ""}`,
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await updateStatusAsync({
            ids: [Number(id)],
            status: newStatus,
          })) as { res_status_code?: string; res_message?: string };
          if (res?.res_status_code === "0") {
            message.success(newStatus === 1 ? "已启用" : "已停用");
            fetchList();
          } else {
            message.error(res?.res_message || "操作失败");
          }
        } catch (e) {
          message.error("操作失败");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const openAddModal = useCallback(async () => {
    form.resetFields();
    setModalOpen(true);
    try {
      const [clientRes, userRes] = await Promise.all([
        getAllClient({}),
        getUserDropdownByLoginId(),
      ]);
      const clientData = clientRes as {
        res_status_code?: string;
        res_content?: { clientId?: string; ClientId?: string; clientName?: string; ClientName?: string }[];
      };
      if (clientData?.res_status_code === "0" && Array.isArray(clientData?.res_content)) {
        const list = clientData.res_content
          .map((r: any) => ({
            clientId: r.clientId ?? r.ClientId ?? "",
            clientName: r.clientName ?? r.ClientName ?? "",
          }))
          .filter((r: any) => r.clientId);
        setClientList(list);
      }
      // 用户下拉（LoginId）- 返回 BaseResultKV[]，即 [{ key, value }] 或 [{ Key, Value }]，兼容历史格式
      const userData = userRes as {
        res_status_code?: string;
        res_content?: { key?: string; value?: string; Key?: string; Value?: string }[];
      };
      if (userData?.res_status_code === "0" && Array.isArray(userData?.res_content)) {
        const list = userData.res_content
          .map((u: any) => ({
            loginId: u.code ?? u.code ?? "",
            userName: u.name ?? u.name ?? "",
          }))
          .filter((u) => u.loginId);
        setUserList(list);
      }
    } catch (e) {
      console.error("获取下拉数据失败", e);
    }
  }, [form]);

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const res = (await addAsync({
        loginId: values.user?.trim?.() ?? values.LoginId ?? values.user,
        clientId: values.system ?? values.ClientId,
      })) as { res_status_code?: string; res_message?: string };
      if (res?.res_status_code === "0") {
        message.success(res?.res_message || "新建成功");
        setModalOpen(false);
        fetchList();
      } else {
        message.error(res?.res_message || "新建失败");
      }
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      message.error("新建失败");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleBatchStatus = async (status: 0 | 1) => {
    if (selectedRows.length === 0) {
      message.warning("请先勾选要操作的记录");
      return;
    }
    const ids = selectedRows
      .map((r) => r.id ?? r.Id)
      .filter((id) => id != null)
      .map(Number);
    if (ids.length === 0) {
      message.warning("所选记录无有效ID");
      return;
    }
    // 批量启用：所选必须均为停用(0)；批量停用：所选必须均为启用(1)
    const getRowStatus = (r: any) => r.Status ?? r.status;
    const allSameStatus = status === 1
      ? selectedRows.every((r) => getRowStatus(r) === 0 || getRowStatus(r) === "0")
      : selectedRows.every((r) => getRowStatus(r) === 1 || getRowStatus(r) === "1");
    if (!allSameStatus) {
      if (status === 1) {
        message.warning("批量启用仅支持已停用的记录，请取消勾选已启用的数据后重试。");
      } else {
        message.warning("批量停用仅支持已启用的记录，请取消勾选已停用的数据后重试。");
      }
      return;
    }
    const actionText = status === 1 ? "批量启用" : "批量停用";
    Modal.confirm({
      title: "操作确认",
      content: `确定要${actionText}选中的 ${ids.length} 条记录吗？${status === 0 ? "停用后对应用户将无法访问对应系统。" : ""}`,
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await updateStatusAsync({ ids, status })) as {
            res_status_code?: string;
            res_message?: string;
          };
          if (res?.res_status_code === "0") {
            message.success(status === 1 ? "批量启用成功" : "批量停用成功");
            setSelectedRowKeys([]);
            setSelectedRows([]);
            fetchList();
          } else {
            message.error(res?.res_message || "操作失败");
          }
        } catch (e) {
          message.error("操作失败");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const columns = [
    ...TABLE_COLUMNS_CONFIG.map((col) => ({
      title: col.field_name,
      dataIndex: col.field_code,
      key: col.field_code,
      width: col.width,
      render: (_val: unknown, record: any) => {
        const raw = getRecordField(record, col.field_code);
        if (raw == null || raw === "") return "-";
        if ((col as { isTime?: boolean }).isTime) {
          return dayjs(raw as string).isValid()
            ? dayjs(raw as string).format("YYYY-MM-DD HH:mm:ss")
            : String(raw);
        }
        if (col.field_code === "Status_name") {
          const status = record.Status ?? record.status;
          const text = status === 1 || status === "1" ? "启用" : "停用";
          return <Tag color={status === 1 || status === "1" ? "green" : "default"}>{text}</Tag>;
        }
        return String(raw);
      },
    })),
    {
      title: "操作",
      key: "action",
      width: 100,
      fixed: "right" as const,
      render: (_: unknown, record: any) => {
        const status = record.Status ?? record.status;
        const isEnabled = status === 1 || status === "1";
        return (
          <Space size="small">
            {isEnabled ? (
              <Button
                type="link"
                size="small"
                danger
                disabled={actionLoading}
                onClick={() => handleStatusChange(record, 0)}
                style={{ padding: 0 }}
              >
                停用
              </Button>
            ) : (
              <Button
                type="link"
                size="small"
                disabled={actionLoading}
                onClick={() => handleStatusChange(record, 1)}
                style={{ padding: 0 }}
              >
                启用
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="user-system-permissions-page">
      <div className="page-header">
        <h2>人员系统权限</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          新建
        </Button>
      </div>

      <Modal
        title="新建人员系统权限"
        open={modalOpen}
        onOk={handleAddSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitLoading}
        destroyOnClose
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          labelCol={{ flex: "110px" }}
          wrapperCol={{ flex: 1 }}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="user"
            label="用户"
            rules={[{ required: true, message: "请选择用户" }]}
          >
            <Select
              placeholder="请选择用户"
              allowClear
              showSearch
              optionFilterProp="label"
              options={userList.map((u) => ({
                value: u.loginId,
                label: `${u.userName}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="system"
            label="系统"
            rules={[{ required: true, message: "请选择系统" }]}
          >
            <Select
              placeholder="系统"
              allowClear
              showSearch
              optionFilterProp="label"
              options={clientList.map((c) => ({
                value: c.clientId,
                label: c.clientName || c.clientId,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="用户名"
            value={searchParams.LoginId}
            onChange={(e) =>
              setSearchParams({ ...searchParams, LoginId: e.target.value })
            }
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="系统"
            value={searchParams.ClientId === "" ? undefined : searchParams.ClientId}
            onChange={(v) =>
              setSearchParams({ ...searchParams, ClientId: v ?? "" })
            }
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={clientList.map((c) => ({
              value: c.clientId,
              label: c.clientName || c.clientId,
            }))}
          />
          <Select
            placeholder="状态"
            value={searchParams.Status === "" ? undefined : searchParams.Status}
            onChange={(v) =>
              setSearchParams({ ...searchParams, Status: v ?? "" })
            }
            style={{ width: 120 }}
            allowClear
            options={[
              { value: 1, label: "启用" },
              { value: 0, label: "停用" },
            ]}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      <div className="toolbar">
        <Space>
          <Button
            disabled={selectedRowKeys.length === 0 || actionLoading}
            onClick={() => handleBatchStatus(1)}
          >
            批量启用
          </Button>
          <Button
            disabled={selectedRowKeys.length === 0 || actionLoading}
            danger
            onClick={() => handleBatchStatus(0)}
          >
            批量停用
          </Button>
          {selectedRowKeys.length > 0 && (
            <span style={{ color: "#666", fontSize: 13 }}>
              已选 {selectedRowKeys.length} 条
            </span>
          )}
        </Space>
      </div>

      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={list.slice(
          (pagination.current - 1) * pagination.pageSize,
          pagination.current * pagination.pageSize
        )}
        rowKey={(record, index) => getRowId(record, index)}
        loading={loading}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: "small",
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000, y: "calc(100vh - 360px)" }}
      />
    </div>
  );
};

export default UserSystemPermissions;
